import {
    Label,
    useStyles,
    Image,
    Button,
    pxToRem,
} from '@archipro-design/aria';
import { Envelope, ExternalLink } from '@archipro-design/icons';
import ClickToReveal from '@modules/root/component/click-to-reveal/ClickToReveal';

import type { TeamMemberView } from '@modules/professional/type/team-member';

import * as S from './ProfileModal.style';
import { useTracker } from '@archipro-website/tracker';
import type { TrackingEvent } from '@archipro-website/tracker';
import { getImagesSizes } from '~/utils/image-sizes-utils';

export const ProfileModal: React.FC<{
    memberDetails: TeamMemberView;
    website: string | null;
}> = ({ memberDetails, website }) => {
    const { css } = useStyles({
        image: memberDetails.avatarSrc,
    });

    const { avatarSrc, name, jobTitle, phone, id, email, summary } =
        memberDetails;
    const { log } = useTracker();

    const clickLink = (trackingEvent: TrackingEvent) => {
        log(trackingEvent, {
            url: new URL(window.location.href),
            data: {
                ExternalURL: website,
                Source: 'detail, teamMemberModal',
                ItemType: 'TeamMember',
            },
        });
    };

    return (
        <div className={css(S.TeamMemberGrid)}>
            <div
                style={{
                    width: pxToRem(172.5),
                    height: pxToRem(206),
                    position: 'relative',
                }}
            >
                <Image
                    src={avatarSrc}
                    layout={'fill'}
                    width={172.5}
                    height={206}
                    sizes={getImagesSizes({
                        desktop: 172.5,
                        mobile: 172.5,
                    })}
                    alt={memberDetails?.name}
                />
            </div>
            <div className={css(S.TeamMemberContent)}>
                <Label variant="02" className={css(S.TeamMemberName)}>
                    {name}
                </Label>
                {jobTitle && (
                    <Label variant="02" className={css(S.TeamMemberRole)}>
                        {jobTitle}
                    </Label>
                )}
                <Label variant="02" className={css(S.TeamMemberPhone)}>
                    {`Phone   `}
                </Label>
                <div className={css(S.TeamMemberClassTest)}>
                    <ClickToReveal
                        fulltext={phone || ''}
                        label={'Phone'}
                        type={'Phone'}
                        className={css(S.TeamMemberClassTest)}
                        buttonTrackingID={id + '-team-member-phone-reveal'}
                        revealKey={`team-member-${id}`}
                    />
                </div>

                <div className={css(S.TeamMemberLinks)}>
                    <Button
                        transparent={true}
                        color="dark"
                        size={18}
                        variables={{ buttonPadding: 0 }}
                        design={{ marginRight: pxToRem(24) }}
                        as="a"
                        rel={'noopener'}
                        href={`mailto:${email}`}
                        onClick={() => clickLink('EmailReveal')}
                    >
                        <Envelope className={css(S.IconClass)} />
                        Email
                    </Button>
                    {website && (
                        <Button
                            transparent={true}
                            color="dark"
                            size={18}
                            variables={{ buttonPadding: 0 }}
                            as="a"
                            target="_blank"
                            href={website}
                            rel="noopener"
                            onClick={() => clickLink('Website')}
                        >
                            <ExternalLink className={css(S.IconClass)} />
                            Website
                        </Button>
                    )}
                </div>

                {summary && summary !== '' && (
                    <div className={css(S.TeamMemberAboutSection)}>
                        <Label
                            variant="02"
                            className={css(S.AboutSectionHeader)}
                        >
                            {`About ${name}`}
                        </Label>

                        <div
                            className={css(S.TeamMemberAboutContent)}
                            dangerouslySetInnerHTML={{
                                __html: summary,
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
